import { useState, useEffect } from "react";
import type { DeviceStats, AdminUserDetails } from "@/common/model/AdminUserDetails";
import axiosInstance from "@/api/axiosInstance";
import { SupportUploadsSubTab } from "./support";
import { GarminFilesSubTab } from "./garmin";
import { SupportService } from "@/services/SupportService";

interface DeviceDetailsTabProps {
    devices?: DeviceStats[];
    userDetails?: AdminUserDetails;
    onRefresh?: () => void;
    onUploadSelect?: (uploadId: string | null) => void;
}

type SubTab = "devices" | "support" | "garmin";

const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatHeight = (height?: number): string => {
    if (height === undefined || height === null) return "-";
    return `${height.toFixed(1)}m`;
};

// Detect device type (watch or phone) based on brand and model
const getDeviceType = (brand?: string, model?: string): "watch" | "phone" | "web" | "unknown" => {
    const brandLower = brand?.toLowerCase() || "";
    const modelLower = model?.toLowerCase() || "";

    // Web/Browser detection
    if (brandLower === "web" || modelLower === "browser") {
        return "web";
    }

    // Garmin is always a watch
    if (brandLower === "garmin") {
        return "watch";
    }

    // Apple Watch detection
    if (brandLower === "apple" && modelLower.includes("watch")) {
        return "watch";
    }

    // Samsung/Android watch detection
    if (modelLower.includes("watch") || modelLower.includes("galaxy watch") || modelLower.includes("gear")) {
        return "watch";
    }

    // If it's Apple or Android and not a watch, it's likely a phone
    if (brandLower === "apple" || brandLower === "samsung" || brandLower === "google" || brandLower === "android") {
        return "phone";
    }

    return "unknown";
};

// Brand icon component
const BrandIcon: React.FC<{ brand?: string; className?: string }> = ({ brand, className = "w-5 h-5" }) => {
    const brandLower = brand?.toLowerCase() || "";

    // Apple icon
    if (brandLower === "apple") {
        return (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
        );
    }

    // Android icon
    if (brandLower === "android" || brandLower === "samsung" || brandLower === "google" || brandLower === "xiaomi" || brandLower === "huawei" || brandLower === "oneplus") {
        return (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
            </svg>
        );
    }

    // Garmin icon (triangle/mountain shape representing outdoor/sports)
    if (brandLower === "garmin") {
        return (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 19h20L12 2zm0 3.84L18.26 17H5.74L12 5.84zM11 10v4h2v-4h-2zm0 5v2h2v-2h-2z"/>
            </svg>
        );
    }

    // Web/Browser icon
    if (brandLower === "web") {
        return (
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
        );
    }

    // Default device icon
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
    );
};

// Device type icon (watch, phone, web)
const DeviceTypeIcon: React.FC<{ type: "watch" | "phone" | "web" | "unknown"; className?: string }> = ({ type, className = "w-4 h-4" }) => {
    if (type === "watch") {
        return (
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        );
    }

    if (type === "phone") {
        return (
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
        );
    }

    if (type === "web") {
        return (
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        );
    }

    // Unknown - generic device
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
    );
};

// Get brand color
const getBrandColor = (brand?: string): string => {
    const brandLower = brand?.toLowerCase() || "";
    if (brandLower === "apple") return "text-gray-800";
    if (brandLower === "garmin") return "text-blue-600";
    if (brandLower === "samsung" || brandLower === "android" || brandLower === "google") return "text-green-600";
    if (brandLower === "web") return "text-purple-600";
    return "text-gray-600";
};

export const DeviceDetailsTab: React.FC<DeviceDetailsTabProps> = ({ devices, userDetails, onRefresh, onUploadSelect }) => {
    const [isTogglingSupport, setIsTogglingSupport] = useState(false);
    const [isTogglingDiagnostics, setIsTogglingDiagnostics] = useState(false);
    const [expandedDevice, setExpandedDevice] = useState<string | null>(null);
    const [activeSubTab, setActiveSubTab] = useState<SubTab>(() => {
        // Auto-switch to support sub-tab if there's a deep link upload ID
        const deeplinkUploadId = sessionStorage.getItem("deeplink-uploadId");
        return deeplinkUploadId ? "support" : "devices";
    });
    const [supportUploadsCount, setSupportUploadsCount] = useState<number>(0);
    const [garminFilesCount, setGarminFilesCount] = useState<number | null>(null);

    // Load support uploads count on mount
    useEffect(() => {
        if (userDetails?.userId) {
            SupportService.listUserUploads(userDetails.userId, { limit: 1 })
                .then((uploads) => {
                    // The actual count comes from the response length for now
                    // In a real scenario, we'd want a count endpoint
                    return SupportService.listUserUploads(userDetails.userId, { limit: 100 });
                })
                .then((uploads) => setSupportUploadsCount(uploads.length))
                .catch(() => setSupportUploadsCount(0));
        }
    }, [userDetails?.userId]);

    const handleToggleSupportMode = async () => {
        if (!userDetails?.username) return;

        const newStatus = !userDetails.supportModeEnabled;
        setIsTogglingSupport(true);

        try {
            await axiosInstance.get(`/users/supportmode/${encodeURIComponent(userDetails.username)}/${newStatus}`);

            // Auto-enable diagnostics when enabling support mode (writefiles)
            if (newStatus && !userDetails.diagnosticsEnabled) {
                try {
                    await axiosInstance.get(`/users/diagnosticsmode/${encodeURIComponent(userDetails.username)}/true`);
                } catch (diagError) {
                    console.error("Error auto-enabling diagnostics mode:", diagError);
                }
            }

            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error("Error toggling support mode:", error);
            alert("Failed to toggle support mode. Please try again.");
        } finally {
            setIsTogglingSupport(false);
        }
    };

    const handleToggleDiagnosticsMode = async () => {
        if (!userDetails?.username) return;

        const newStatus = !userDetails.diagnosticsEnabled;
        setIsTogglingDiagnostics(true);

        try {
            await axiosInstance.get(`/users/diagnosticsmode/${encodeURIComponent(userDetails.username)}/${newStatus}`);

            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error("Error toggling diagnostics mode:", error);
            alert("Failed to toggle diagnostics mode. Please try again.");
        } finally {
            setIsTogglingDiagnostics(false);
        }
    };

    const toggleDeviceExpand = (uniqueId: string) => {
        setExpandedDevice(expandedDevice === uniqueId ? null : uniqueId);
    };

    const deviceCount = devices?.length || 0;

    return (
        <div className="space-y-6">
            {/* Support Mode Section - at top for easy access */}
            {userDetails && (
                <SupportModeSection
                    userDetails={userDetails}
                    isTogglingSupport={isTogglingSupport}
                    isTogglingDiagnostics={isTogglingDiagnostics}
                    onToggleSupportMode={handleToggleSupportMode}
                    onToggleDiagnosticsMode={handleToggleDiagnosticsMode}
                />
            )}

            {/* Sub-tabs */}
            <div className="border-b border-gray-200">
                <nav className="flex gap-4" aria-label="Sub-tabs">
                    <button
                        onClick={() => setActiveSubTab("devices")}
                        className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                            activeSubTab === "devices"
                                ? "border-cyan-500 text-cyan-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                    >
                        Devices ({deviceCount})
                    </button>
                    <button
                        onClick={() => setActiveSubTab("support")}
                        className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                            activeSubTab === "support"
                                ? "border-cyan-500 text-cyan-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                    >
                        Support Uploads ({supportUploadsCount})
                    </button>
                    {userDetails?.hasGarminConnected && (
                        <button
                            onClick={() => setActiveSubTab("garmin")}
                            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                                activeSubTab === "garmin"
                                    ? "border-cyan-500 text-cyan-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                            Garmin Files{garminFilesCount !== null ? ` (${garminFilesCount})` : ""}
                        </button>
                    )}
                </nav>
            </div>

            {/* Sub-tab Content */}
            {activeSubTab === "devices" && (
                <DevicesContent
                    devices={devices}
                    expandedDevice={expandedDevice}
                    onToggleExpand={toggleDeviceExpand}
                />
            )}
            {activeSubTab === "support" && userDetails?.userId && (
                <SupportUploadsSubTab
                    userId={userDetails.userId}
                    onCountChange={setSupportUploadsCount}
                    onUploadSelect={onUploadSelect}
                />
            )}
            {activeSubTab === "garmin" && userDetails?.userId && (
                <GarminFilesSubTab
                    userId={userDetails.userId}
                    onCountChange={setGarminFilesCount}
                />
            )}
        </div>
    );
};

// Devices content component (extracted from original)
interface DevicesContentProps {
    devices?: DeviceStats[];
    expandedDevice: string | null;
    onToggleExpand: (uniqueId: string) => void;
}

const DevicesContent: React.FC<DevicesContentProps> = ({ devices, expandedDevice, onToggleExpand }) => {
    if (!devices || devices.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-sm text-gray-500 italic">No device information available</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header with device count */}
            <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Ordered by most recent session</p>
            </div>

            {/* Device List */}
            <div className="space-y-3">
                {devices.map((device, index) => (
                    <DeviceCard
                        key={device.uniqueId || index}
                        device={device}
                        isExpanded={expandedDevice === (device.uniqueId || String(index))}
                        onToggle={() => onToggleExpand(device.uniqueId || String(index))}
                        isFirst={index === 0}
                    />
                ))}
            </div>

            {/* Legend */}
            <div className="pt-4 border-t border-gray-200">
                <h4 className="text-xs font-medium text-gray-700 mb-2">Field Descriptions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-500">
                    <div><span className="font-medium">Sessions:</span> Total sessions recorded from this device</div>
                    <div><span className="font-medium">Device Logins:</span> Number of times logged in from this device</div>
                    <div><span className="font-medium">Hidden Sessions:</span> Freemium sessions not visible on leaderboard</div>
                    <div><span className="font-medium">Max/Min Height:</span> Best and lowest jump heights from this device</div>
                    <div><span className="font-medium">First/Last Session:</span> Activity period on this device</div>
                    <div><span className="font-medium">First/Last Login:</span> When device was registered/last used</div>
                </div>
            </div>
        </div>
    );
};

interface DeviceCardProps {
    device: DeviceStats;
    isExpanded: boolean;
    onToggle: () => void;
    isFirst: boolean;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device, isExpanded, onToggle, isFirst }) => {
    const deviceType = getDeviceType(device.brand, device.model);
    const brandColor = getBrandColor(device.brand);

    const getDeviceTypeLabel = (type: "watch" | "phone" | "web" | "unknown"): string => {
        switch (type) {
            case "watch": return "Watch";
            case "phone": return "Phone";
            case "web": return "Browser";
            default: return "Device";
        }
    };

    return (
        <div className={`bg-white rounded-lg border ${isFirst ? "border-cyan-300 ring-1 ring-cyan-100" : "border-gray-200"}`}>
            {/* Collapsed View - Always visible */}
            <button
                onClick={onToggle}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    {/* Brand Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isFirst ? "bg-cyan-100" : "bg-gray-100"}`}>
                        <BrandIcon brand={device.brand} className={`w-5 h-5 ${isFirst ? "text-cyan-600" : brandColor}`} />
                    </div>

                    {/* Device Info */}
                    <div className="text-left">
                        <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">
                                {device.brand || "Unknown"} {device.model || "Device"}
                            </p>
                            {/* Device Type Badge */}
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1 ${
                                deviceType === "watch" ? "bg-blue-100 text-blue-700" :
                                deviceType === "phone" ? "bg-green-100 text-green-700" :
                                deviceType === "web" ? "bg-purple-100 text-purple-700" :
                                "bg-gray-100 text-gray-700"
                            }`}>
                                <DeviceTypeIcon type={deviceType} className="w-3 h-3" />
                                {getDeviceTypeLabel(deviceType)}
                            </span>
                            {isFirst && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-cyan-100 text-cyan-700 rounded-full">
                                    Most Recent
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-gray-500">
                            {device.sessionCount || 0} sessions • Last: {formatDate(device.lastSession)}
                        </p>
                    </div>
                </div>

                {/* Expand/Collapse Icon */}
                <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Expanded View */}
            {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                        {/* Session Statistics */}
                        <StatItem
                            label="Total Sessions"
                            value={String(device.sessionCount || 0)}
                            icon={<SessionIcon />}
                            tooltip="Number of surf/kite sessions recorded from this device"
                        />
                        <StatItem
                            label="Device Logins"
                            value={String(device.deviceLogins || 0)}
                            icon={<LoginIcon />}
                            tooltip="Number of times the user logged in from this device"
                        />
                        <StatItem
                            label="Hidden Sessions"
                            value={String(device.notVisibleFreemiumSessionsCount || 0)}
                            icon={<HiddenIcon />}
                            tooltip="Freemium sessions not visible on the public leaderboard"
                        />

                        {/* Height Stats */}
                        <StatItem
                            label="Max Height"
                            value={formatHeight(device.maxHeight)}
                            icon={<HeightIcon />}
                            tooltip="Highest jump recorded from this device"
                        />
                        <StatItem
                            label="Min Height"
                            value={formatHeight(device.minHeight)}
                            icon={<HeightIcon />}
                            tooltip="Lowest recorded jump from this device"
                        />

                        {/* Unique ID */}
                        <StatItem
                            label="Device ID"
                            value={device.uniqueId ? `...${device.uniqueId.slice(-8)}` : "-"}
                            icon={<IdIcon />}
                            tooltip={device.uniqueId || "Unique device identifier"}
                        />
                    </div>

                    {/* Date Information */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Session Activity</p>
                                <div className="text-xs">
                                    <span className="text-gray-700">First: </span>
                                    <span className="text-gray-900">{formatDate(device.firstSession)}</span>
                                </div>
                                <div className="text-xs">
                                    <span className="text-gray-700">Last: </span>
                                    <span className="text-gray-900">{formatDate(device.lastSession)}</span>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Device Registration</p>
                                <div className="text-xs">
                                    <span className="text-gray-700">First Login: </span>
                                    <span className="text-gray-900">{formatDate(device.firstDeviceLogin)}</span>
                                </div>
                                <div className="text-xs">
                                    <span className="text-gray-700">Last Login: </span>
                                    <span className="text-gray-900">{formatDate(device.lastDeviceLogin)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

interface StatItemProps {
    label: string;
    value: string;
    icon: React.ReactNode;
    tooltip?: string;
}

const StatItem: React.FC<StatItemProps> = ({ label, value, icon, tooltip }) => (
    <div className="group relative">
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                {icon}
            </div>
            <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-sm font-medium text-gray-900">{value}</p>
            </div>
        </div>
        {tooltip && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                {tooltip}
            </div>
        )}
    </div>
);

interface SupportModeSectionProps {
    userDetails: AdminUserDetails;
    isTogglingSupport: boolean;
    isTogglingDiagnostics: boolean;
    onToggleSupportMode: () => void;
    onToggleDiagnosticsMode: () => void;
}

const SupportModeSection: React.FC<SupportModeSectionProps> = ({
    userDetails,
    isTogglingSupport,
    isTogglingDiagnostics,
    onToggleSupportMode,
    onToggleDiagnosticsMode,
}) => (
    <div className="pb-4 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Debug Settings</h3>
        <div className="space-y-3">
            {/* Diagnostics Mode - Lightweight session stats */}
            <div className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Diagnostics Mode</p>
                        <p className="text-sm font-medium text-gray-900">
                            {userDetails.diagnosticsEnabled ? "Enabled" : "Disabled"}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Lightweight session stats (+1-3%/hr battery)
                        </p>
                    </div>
                </div>
                <button
                    onClick={onToggleDiagnosticsMode}
                    disabled={isTogglingDiagnostics}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        userDetails.diagnosticsEnabled ? "bg-cyan-600" : "bg-gray-200"
                    }`}
                >
                    <span className="sr-only">Toggle diagnostics mode</span>
                    <span
                        className="inline-block h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out"
                        style={{
                            transform: userDetails.diagnosticsEnabled ? "translateX(24px)" : "translateX(4px)",
                        }}
                    />
                </button>
            </div>

            {/* Support Mode (Writefiles) - Heavy algo trace files */}
            <div className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Trace Files (Support Mode)</p>
                        <p className="text-sm font-medium text-gray-900">
                            {userDetails.supportModeEnabled ? "Enabled" : "Disabled"}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Heavy algo trace files (+10-15%/hr battery) - user can disable
                        </p>
                    </div>
                </div>
                <button
                    onClick={onToggleSupportMode}
                    disabled={isTogglingSupport}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        userDetails.supportModeEnabled ? "bg-cyan-600" : "bg-gray-200"
                    }`}
                >
                    <span className="sr-only">Toggle support mode</span>
                    <span
                        className="inline-block h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out"
                        style={{
                            transform: userDetails.supportModeEnabled ? "translateX(24px)" : "translateX(4px)",
                        }}
                    />
                </button>
            </div>
        </div>
    </div>
);

// Icon Components
const SessionIcon = () => (
    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
);

const LoginIcon = () => (
    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
    </svg>
);

const HiddenIcon = () => (
    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
);

const HeightIcon = () => (
    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
    </svg>
);

const IdIcon = () => (
    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
    </svg>
);
